<template lang="pug">
.v-viewer360.viewer360(v-if="data")
	.viewer360__container
		.viewer360__title(v-if="data.title") {{data.title}}
		.viewer360__wrapper(v-if="slides && slides.length")
			template(v-if="isVisible")
				template(v-if="isiOS && isLoadCanvasImages")
					img.viewer360__img.viewer360__img--active(v-if="preloadedImagesArray.length"
							:src="preloadedImagesArray[normalizedIndex]"
							alt=""
							loading="eager"
							decoding="sync")
				template(v-if="!isiOS")
					img.viewer360__img(v-for="(slide, slideIndex) in slides"
							:key="slideIndex"
							:src="slide"
							:class="{'viewer360__img--active': slideIndex === normalizedIndex, 'viewer360__img--loading': !isLoadImages }"
							alt=""
							@load="checkImageLoaded"
							@error="handleImageError")
			img.viewer360__circle(v-if="data.circleImg"
					:src="data.circleImg"
					alt=""
					loading="lazy")
			.viewer360__over-wrap(@mousedown="startDragging"
					@mouseup="stopDragging"
					@mouseleave="stopDragging"
					@touchstart="startDragging"
					@touchend="stopDragging"
					@touchmove.prevent="handleTouchMove")
				v-loader.viewer360__loader(:loading="computedLoader")
		.viewer360__colors(v-if="data.filters && data.filters.length")
			v-field(v-model="data.filters[0].value" :field="{...data.filters[0]}" @update:model-value="filterChanged")
</template>

<script>
import sourceMixin from '../components/mixins/v-mixin-source.vue';
import isMobMixin from '../components/mixins/v-mixin-is-mob.vue';

export default {
	mixins: [
		sourceMixin,
		isMobMixin,
	],

	data() {
		return {
			slides: [],
			data: null,
			currentNumber: 0,
			maxNumber: null,
			slowNumber: 3,
			isDragging: false,
			lastMouseX: 0,
			isLoadImages: false,
			isLoadCanvasImages: false,
			imagesLoaded: 0,
			totalImages: 0,
			isVisible: false,
			observer: null,
			preloadedImagesArray: [],
		};
	},

	created() {
		window.addEventListener('mousemove', this.handleMouseMove);
		window.addEventListener('mouseup', this.stopDragging);
		window.addEventListener('mouseleave', this.stopDragging);
	},

	beforeUnmount() {
		this.isLoadImages = false;
		this.isLoadCanvasImages = false;
		window.removeEventListener('mousemove', this.handleMouseMove);
		window.removeEventListener('mouseup', this.stopDragging);
		window.removeEventListener('mouseleave', this.stopDragging);
		if (this.observer) {
			this.observer.disconnect();
		}
	},

	computed: {
		normalizedIndex() {
			const integerCurrentNumber = Math.floor(this.currentNumber);
			if (integerCurrentNumber < 0) {
				return this.slides.length + integerCurrentNumber;
			}
			return integerCurrentNumber;
		},

		computedLoader() {
			return this.isiOS ? !this.isLoadCanvasImages : !this.isLoadImages
		}
	},

	methods: {
		async onObserver() {
			try {
				if (!this.$el) {
					console.warn('Элемент this.$el не определен.');
					if(this.isiOS){
						await this.preloadImages(this.slides);
					}
					this.isVisible = true;
					return;
				}
				if (typeof IntersectionObserver === 'undefined') {
					console.warn('IntersectionObserver не поддерживается.');
					if(this.isiOS){
						await this.preloadImages(this.slides);
					}
					this.isVisible = true;
					return;
				}
				this.observer = new IntersectionObserver((entries) => {
					entries.forEach(async (entry) => {
						if (entry.isIntersecting) {
							if(this.isiOS){
								await this.preloadImages(this.slides);
							}
							this.isVisible = true;
							this.observer.unobserve(entry.target);
						}
					});
				});
				this.observer.observe(this.$el);
			} catch (error) {
				console.error('Ошибка при создании IntersectionObserver:', error);
				if(this.isiOS){
					await this.preloadImages(this.slides);
				}
				this.isVisible = true;
			}
		},

		async preloadImages(array) {
			this.isLoadCanvasImages = false;
			this.preloadedImagesArray = await Promise.all(
				array.map((src) => {
					return new Promise((resolve) => {
						const img = new Image();
						img.src = src;
						img.crossOrigin = "anonymous";
						img.onload = () => {
							const canvas = document.createElement('canvas');
							const ctx = canvas.getContext('2d');
							canvas.width = img.width;
							canvas.height = img.height;
							ctx.drawImage(img, 0, 0, img.width, img.height);
							const dataURL = canvas.toDataURL('image/webp');
							resolve(dataURL);
						};
						img.onerror = () => resolve(null);
					});
				})
			);
			this.isLoadCanvasImages = true
		},

		checkImageLoaded() {
			this.imagesLoaded++;
			if (this.imagesLoaded === this.totalImages) {
				this.isLoadImages = true;
			}
		},

		handleImageError() {
			this.imagesLoaded++;
			console.error('Ошибка загрузки изображения');
		},

		startDragging(event) {
			if (this.computedLoader) {
				return;
			}
			this.isDragging = true;
			this.lastMouseX = event.clientX || event.touches[0].clientX;
		},

		stopDragging() {
			this.isDragging = false;
		},

		handleMouseMove(event) {
			if (!this.isDragging || this.computedLoader) return;
			if (!this.isDragging) return;
			const movement = this.convertMovement((event.clientX || event.touches[0].clientX) - this.lastMouseX);
			this.lastMouseX = event.clientX || event.touches[0].clientX;

			this.currentNumber -= (movement / this.slowNumber);

			if ((this.currentNumber + (movement / this.slowNumber)) > this.maxNumber) {
				this.currentNumber = 0;
			}
			if ((this.currentNumber - (movement / this.slowNumber)) < -this.maxNumber) {
				this.currentNumber = 0;
			}
		},

		handleTouchMove(event) {
			if (this.computedLoader) {
				return;
			}
			this.handleMouseMove(event);
		},

		convertMovement(movement) {
			if (movement >= 1) {
				return 1;
			}
			if (movement <= -1) {
				return -1;
			}
			return 0;
		},

		getSortedImages(items) {
			if (!items || !Array.isArray(items)) {
				return [];
			}
			if (items.every(item => typeof item === 'string')) {
				return items;
			}
			return [...items]
				.sort((a, b) => a.number - b.number)
				.map(item => item.image);
		},

		extrapolate(number) {
			const point1 = {x: 15, y: 3};
			const point2 = {x: 30, y: 1.5};

			if (number < point1.x) {
				const slope = (point2.y - point1.y) / (point2.x - point1.x);
				const result = point1.y + slope * (number - point1.x);
				return Math.max(result, 1);
			} else if (number > point2.x) {
				const slope = (point2.y - point1.y) / (point2.x - point1.x);
				const result = point2.y + slope * (number - point2.x);
				return Math.max(result, 1);
			} else {
				const slope = (point2.y - point1.y) / (point2.x - point1.x);
				const result = point1.y + slope * (number - point1.x);
				return Math.max(result, 1);
			}
		},

		createFilterValue() {
			if (this.data && this.data.filters) {
				let result = {};
				this.data.filters.forEach(filter => {
					result[filter.name] = filter.value;
				});
				return result;
			} else {
				return null;
			}
		},

		filterChanged() {
			this.requestDataLoad();
			this.isLoadImages = false;
			this.isLoadCanvasImages = false;
		},

		requestDataLoad(dataSetOptions) {
			this.dataLoad({
				url: this.data.remote,
				delay: true,
				data: {
					filter: this.createFilterValue()
				}
			}, dataSetOptions);
		},

		dataSet(data) {
			this.currentNumber = 0;
			const {items, ...others} = data;
			this.data = others;
			if (items) {
				this.slides = this.getSortedImages(items);
				this.maxNumber = this.slides?.length - 1;
				this.slowNumber = this.extrapolate(this.slides?.length);
				this.totalImages = this.slides.length;
				this.imagesLoaded = 0;
			}
			this.$nextTick(() => {
				this.onObserver();
			});
		}
	}
};
</script>